import { DateTime } from "luxon";
import { BuilderConfigDocument, LanguageCode, PrivacyPolicy } from "./index";
import { updateStringVars } from "./preregistration";

export type Languages = LanguageCode | "en";

export const generatePrivacyText = <T>(
  config: Partial<BuilderConfigDocument<T | DateTime>>,
  language: Languages = "en",
) => {
  const stringReplacements = [
    { key: "artistName", value: config.artistName || "" },
  ];
  const separateTerms = config.fields?.privacyPolicies?.content
    ?.filter(({ isSeparate }) => isSeparate)
    ?.map(
      (p) =>
        `I agree to the <a href='${p.url}' target='_blank'>${p.name} Privacy Policy</a>.`,
    );

  const policies = config.fields?.privacyPolicies?.content?.filter(
    ({ isSeparate }) => !isSeparate,
  );

  const optins =
    language in smsOptIns
      ? smsOptIns[language as keyof typeof smsOptIns]
      : smsOptIns.en;
  const sms = updateStringVars(optins, stringReplacements);
  const terms = getOptInLabel(language, config.artistName || "", policies);

  return { sms, terms, separateTerms };
};

const smsOptIns = {
  en: "I'd like to receive text or messaging service messages from {artistName}, SET, or via {artistName}'s partners or representatives. Consent is not a condition of purchase. Message data rates may apply.",
  es: "Me gustaría recibir mensajes de texto de {artistName}, SET, o a través de los socios o representantes de {artistName}. El consentimiento no es una condición de compra. Pueden aplicar tarifas de datos de mensajes.",
  fr: "Je souhaite recevoir des messages texte de {artistName}, SET, ou via les partenaires ou représentants de {artistName}. Le consentement n'est pas une condition d'achat. Des frais de données de message peuvent s'appliquer.",
  de: "Ich möchte Text- oder Messaging-Nachrichten von {artistName}, SET oder über {artistName}s Partner oder Vertreter erhalten. Die Zustimmung ist keine Kaufbedingung. Es können Nachrichtendatengebühren anfallen.",
  pt: "Gostaria de receber mensagens de texto ou de aplicativos de mensagens de {artistName}, da SET ou dos parceiros ou representantes de {artistName}. O consentimento não é condição para compra. Podem ser cobradas tarifas de dados.",
};

const label = {
  privacyPolicy: {
    en: "Privacy Policy",
    es: "política de privacidad",
    fr: "politique de confidentialité",
    de: "Datenschutzrichtlinie",
    pt: "política de privacidade",
  },
  termsOfUse: {
    en: "Terms of Use",
    es: "términos de uso",
    fr: "conditions d'utilisation",
    de: "Nutzungsbedingungen",
    pt: "termos de uso",
  },
};

// before common nouns
export const commonNounConjunction = {
  en: "and",
  es: "y la",
  fr: "et la",
  de: "und",
  pt: "e a",
};

// before proper nouns / links
export const properNounConjunction = {
  en: "and",
  es: "y",
  fr: "et",
  de: "und",
  pt: "e",
};

const statements = {
  statement1: {
    en: `I am at least 13 years of age and agree to share the above information with {artistName} and SET. I agree to SET's {setAgreement} as well as <a href='{artistUrl}' target='_blank'>{artistName}'s Privacy Policy</a>.`,
    es: `Soy mayor de 13 años y acepto compartir la información anterior con {artistName} y SET. Acepto los {setAgreement} de SET, así como la <a href='{artistUrl}' target='_blank'>Política de privacidad de {artistName}</a>.`,
    fr: `J'ai au moins 13 ans et j'accepte de partager les informations ci-dessus avec {artistName} et SET. J'accepte les {setAgreement} de SET ainsi que la <a href='{artistUrl}' target='_blank'>politique de confidentialité de {artistName}</a>.`,
    de: `Ich bin mindestens 13 Jahre alt und erkläre mich damit einverstanden, die oben genannten Informationen mit {artistName} und SET zu teilen. Ich akzeptiere die {setAgreement} von SET sowie die <a href='{artistUrl}' target='_blank'>Datenschutzrichtlinie von {artistName}</a>.`,
    pt: `Tenho pelo menos 13 anos e concordo em compartilhar as informações acima com {artistName} e a SET. Aceito os {setAgreement} da SET, bem como a <a href='{artistUrl}' target='_blank'>Política de Privacidade de {artistName}</a>.`,
  },
  statement2: {
    en: `I am at least 13 years of age and agree to SET's {setAgreement} as well as the privacy policies of {additionalLinks}.`,
    es: `Soy mayor de 13 años y acepto los {setAgreement} de SET, así como las políticas de privacidad de {additionalLinks}.`,
    fr: `J'ai au moins 13 ans et j'accepte les {setAgreement} de SET ainsi que les politiques de confidentialité de {additionalLinks}.`,
    de: `Ich bin mindestens 13 Jahre alt und akzeptiere die {setAgreement} von SET sowie die Datenschutzrichtlinien von {additionalLinks}.`,
    pt: `Tenho pelo menos 13 anos e aceito os {setAgreement} da SET, bem como as políticas de privacidade de {additionalLinks}.`,
  },
  statement3: {
    en: `I am at least 13 years of age and agree to share the above information with {artistName} and SET. I agree to SET's {setAgreement} as well as the privacy policies of {additionalLinks}.`,
    es: `Soy mayor de 13 años y acepto compartir la información anterior con {artistName} y SET. Acepto los {setAgreement} de SET, así como las políticas de privacidad de {additionalLinks}.`,
    fr: `J'ai au moins 13 ans et j'accepte de partager les informations ci-dessus avec {artistName} et SET. J'accepte les {setAgreement} de SET ainsi que les politiques de confidentialité de {additionalLinks}.`,
    de: `Ich bin mindestens 13 Jahre alt und erkläre mich damit einverstanden, die oben genannten Informationen mit {artistName} und SET zu teilen. Ich akzeptiere die {setAgreement} von SET sowie die Datenschutzrichtlinien von {additionalLinks}.`,
    pt: `Tenho pelo menos 13 anos e concordo em compartilhar as informações acima com {artistName} e a SET. Aceito os {setAgreement} da SET, bem como as políticas de privacidade de {additionalLinks}.`,
  },
  statement4: {
    en: `I am at least 13 years of age and agree to share the above information with {artistName} and SET and agree to SET's {setAgreement}.`,
    es: `Soy mayor de 13 años y acepto compartir la información anterior con {artistName} y SET y acepto los {setAgreement} de SET.`,
    fr: `J'ai au moins 13 ans et j'accepte de partager les informations ci-dessus avec {artistName} et SET et j'accepte les {setAgreement} de SET.`,
    de: `Ich bin mindestens 13 Jahre alt und erkläre mich damit einverstanden, die oben genannten Informationen mit {artistName} und SET zu teilen und akzeptiere die {setAgreement} von SET.`,
    pt: `Tenho pelo menos 13 anos, concordo em compartilhar as informações acima com {artistName} e a SET e aceito os {setAgreement} da SET.`,
  },
};

type StatementType = "statement1" | "statement2" | "statement3" | "statement4";

const setAgreement = (lang: Languages) => `<a href="#" onclick="(function(e){
  const event = new Event('setfan-show-terms', {bubbles: true});
  e.target.dispatchEvent(event);
  e.stopPropagation();
  return false;
})(arguments[0]);return false;">${label.termsOfUse[lang]}</a>
 ${commonNounConjunction[lang]} <a href="#" onclick="(function(e){
  const event = new Event('setfan-show-privacy', {bubbles: true});
  e.target.dispatchEvent(event);
  e.stopPropagation();
  return false;
})(arguments[0]);return false;">${label.privacyPolicy[lang]}</a>`;

const createEntityLinks = (
  entities: PrivacyPolicy[],
  properNounConjunction: string,
) => {
  return entities.map(
    (entity, index) =>
      `${
        !!entities?.length &&
        entities.length > 1 &&
        index === entities.length - 1
          ? `${properNounConjunction} `
          : ""
      }<a href='${entity.url}' target='_blank'>${entity.name}</a>`,
  );
};

export const getOptInLabel = (
  lang: Languages,
  artistName: string,
  values?: PrivacyPolicy[],
) => {
  const firstArtistPolicyIndex = (values || [])?.findIndex(
    ({ entity }) => entity === "artist",
  );
  const artistPolicy =
    firstArtistPolicyIndex >= 0 ? values?.[firstArtistPolicyIndex] : undefined;
  const additionalPolicies = values?.filter(
    (a, i) => i !== firstArtistPolicyIndex,
  );

  const getStatement = (statement: StatementType, additionalLinks?: string) => {
    const mapObj: { [key: string]: string } = {
      "{setAgreement}": setAgreement(lang),
      "{artistName}": artistName,
      "{artistUrl}": artistPolicy?.url || "",
      "{additionalLinks}": additionalLinks || "",
    };
    const regex = new RegExp(Object.keys(mapObj).join("|"), "gi");
    let selectedStatement = statements[statement][lang];
    if (!selectedStatement) {
      selectedStatement = statements[statement].en;
    }
    selectedStatement = selectedStatement.replace(
      regex,
      (matched) => mapObj[matched],
    );
    return selectedStatement;
  };

  // No policy links provided for artist or additional entities
  if (!values?.length) {
    return getStatement("statement4");
  }

  // artist has privacy policy link, but no additional entities present.
  if (!additionalPolicies?.length && artistPolicy?.url) {
    return getStatement("statement1");
  }

  // additional entities have included a privacy policy link
  const additionalLinks = createEntityLinks(
    values,
    properNounConjunction[lang],
  );
  // more than 2 entities
  if (values.length > 2) {
    return getStatement("statement2", additionalLinks.join(", "));
  }
  // 1 or 2 entities
  return getStatement("statement3", additionalLinks.join(" "));
};
